import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { Subject } from 'rxjs';
import { mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import { BCSaaSOrganizationPersonRoleEntity } from '@bluecypress/bcsaas-entities';
import { IzzySLAPolicyEntity, IzzyTicketTeamEntity } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

type Priority = 'Low' | 'Medium' | 'High' | 'Critical' | 'All';

/** A policy plus inline edit state. */
interface PolicyDisplay {
  policy: IzzySLAPolicyEntity;
  isEditing: boolean;
  form: PolicyForm;
}

interface PolicyForm {
  name: string;
  priority: Priority;
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  businessHoursOnly: boolean;
  escalationPersonId: string;
  escalationTeamId: string;
  sortOrder: number;
  status: 'Active' | 'Inactive';
}

/**
 * SLA Policies admin panel (PRD v4.0 §5.2 / §9). Org-scoped management of SLAPolicy rows — the
 * first-response/resolution targets the SLA engine (SLAService) matches to tickets by priority.
 * Admin configuration, so it lives under Administration.
 */
@Component({
  standalone: false,
  selector: 'izzy-sla-policies',
  templateUrl: './sla-policies.component.html',
  styleUrls: ['./sla-policies.component.css'],
})
export class SLAPoliciesComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  protected policies: PolicyDisplay[] = [];
  protected people: mjBizAppsCommonPersonEntity[] = [];
  protected teams: IzzyTicketTeamEntity[] = [];

  protected readonly priorities: Priority[] = ['All', 'Low', 'Medium', 'High', 'Critical'];

  protected isLoading = true;
  protected isSaving = false;
  protected errorMessage = '';
  protected successMessage = '';

  protected isCreating = false;
  protected createForm: PolicyForm = this.emptyForm();

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private emptyForm(): PolicyForm {
    return {
      name: '',
      priority: 'All',
      firstResponseMinutes: null,
      resolutionMinutes: null,
      businessHoursOnly: false,
      escalationPersonId: '',
      escalationTeamId: '',
      sortOrder: 0,
      status: 'Active',
    };
  }

  protected async loadData(): Promise<void> {
    if (!this.organizationId) {
      this.policies = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await this.loadPeople();
      await this.loadTeams();

      const rv = new RunView();
      const res = await rv.RunView<IzzySLAPolicyEntity>({
        EntityName: 'SLA Policies',
        ExtraFilter: `OrganizationID = '${this.organizationId}'`,
        OrderBy: 'SortOrder ASC, Name ASC',
        ResultType: 'entity_object',
      });
      const policies = res.Success ? res.Results : [];
      this.policies = policies.map(p => ({ policy: p, isEditing: false, form: this.toForm(p) }));

      this.statusChange.emit(
        this.policies.length === 0
          ? { status: 'none', tooltip: 'No SLA policies' }
          : { status: 'good', tooltip: `${this.policies.filter(p => p.policy.Status === 'Active').length} active policy(ies)` }
      );
    } catch (error) {
      console.error('SLAPolicies: failed to load', error);
      this.errorMessage = 'Failed to load SLA policies.';
    } finally {
      this.isLoading = false;
    }
  }

  /** Org People (for the escalation-person picker). */
  private async loadPeople(): Promise<void> {
    const rv = new RunView();
    const oprRes = await rv.RunView<BCSaaSOrganizationPersonRoleEntity>({
      EntityName: 'BC: Organization Person Roles',
      ExtraFilter: `OrganizationID = '${this.organizationId}'`,
      Fields: ['PersonID'],
    });
    const personIds = Array.from(
      new Set((oprRes.Success ? oprRes.Results : []).map(r => r.PersonID).filter(Boolean))
    );
    if (personIds.length === 0) {
      this.people = [];
      return;
    }
    const list = personIds.map(id => `'${id}'`).join(', ');
    const peopleRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${list})`,
      OrderBy: 'LastName ASC',
      ResultType: 'entity_object',
    });
    this.people = peopleRes.Success ? peopleRes.Results : [];
  }

  /** Org teams (for the escalation-team picker). */
  private async loadTeams(): Promise<void> {
    const rv = new RunView();
    const res = await rv.RunView<IzzyTicketTeamEntity>({
      EntityName: 'Ticket Teams',
      ExtraFilter: `OrganizationID = '${this.organizationId}' AND Status = 'Active'`,
      OrderBy: 'Name ASC',
      ResultType: 'entity_object',
    });
    this.teams = res.Success ? res.Results : [];
  }

  private toForm(p: IzzySLAPolicyEntity): PolicyForm {
    return {
      name: p.Name,
      priority: p.Priority as Priority,
      firstResponseMinutes: p.FirstResponseTargetMinutes,
      resolutionMinutes: p.ResolutionTargetMinutes,
      businessHoursOnly: !!p.BusinessHoursOnly,
      escalationPersonId: p.EscalationPersonID || '',
      escalationTeamId: p.EscalationTeamID || '',
      sortOrder: p.SortOrder,
      status: p.Status as 'Active' | 'Inactive',
    };
  }

  // ========== Display helpers ==========

  personName(personId: string | null): string {
    if (!personId) return '';
    const p = this.people.find(x => UUIDsEqual(x.ID, personId));
    if (!p) return 'Unknown';
    return p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Unknown';
  }

  teamName(teamId: string | null): string {
    if (!teamId) return '';
    return this.teams.find(t => UUIDsEqual(t.ID, teamId))?.Name || 'Unknown';
  }

  /** Friendly duration label from minutes (e.g. 90 → "1h 30m", 1440 → "1d"). */
  formatMinutes(min: number | null): string {
    if (min == null) return '—';
    if (min < 60) return `${min}m`;
    const days = Math.floor(min / 1440);
    const hours = Math.floor((min % 1440) / 60);
    const mins = min % 60;
    return [days ? `${days}d` : '', hours ? `${hours}h` : '', mins ? `${mins}m` : '']
      .filter(Boolean)
      .join(' ') || '0m';
  }

  // ========== Create ==========

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.emptyForm();
  }

  cancelCreate(): void {
    this.isCreating = false;
  }

  async saveCreate(): Promise<void> {
    if (!this.validate(this.createForm)) return;
    if (!this.organizationId) return;
    this.isSaving = true;
    try {
      const md = new Metadata();
      const p = await md.GetEntityObject<IzzySLAPolicyEntity>('SLA Policies');
      p.NewRecord();
      p.OrganizationID = this.organizationId;
      this.applyForm(p, this.createForm);
      if (!await p.Save()) {
        this.errorMessage = p.LatestResult?.CompleteMessage || 'Failed to create policy.';
        return;
      }
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('SLA policy created');
    } catch (error) {
      console.error('SLAPolicies: create failed', error);
      this.errorMessage = 'Failed to create policy.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Edit ==========

  startEdit(d: PolicyDisplay): void {
    d.isEditing = true;
    d.form = this.toForm(d.policy);
  }

  cancelEdit(d: PolicyDisplay): void {
    d.isEditing = false;
  }

  async saveEdit(d: PolicyDisplay): Promise<void> {
    if (!this.validate(d.form)) return;
    this.isSaving = true;
    try {
      this.applyForm(d.policy, d.form);
      if (!await d.policy.Save()) {
        this.errorMessage = d.policy.LatestResult?.CompleteMessage || 'Failed to update policy.';
        return;
      }
      d.isEditing = false;
      this.showSuccess('SLA policy updated');
    } catch (error) {
      console.error('SLAPolicies: edit failed', error);
      this.errorMessage = 'Failed to update policy.';
    } finally {
      this.isSaving = false;
    }
  }

  async confirmDelete(d: PolicyDisplay): Promise<void> {
    this.isSaving = true;
    try {
      if (!await d.policy.Delete()) {
        this.errorMessage = 'Failed to delete policy.';
        return;
      }
      this.policies = this.policies.filter(x => x !== d);
      this.showSuccess('SLA policy deleted');
    } catch (error) {
      console.error('SLAPolicies: delete failed', error);
      this.errorMessage = 'Failed to delete policy.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Shared ==========

  private validate(form: PolicyForm): boolean {
    if (!form.name.trim()) {
      this.errorMessage = 'Policy name is required.';
      return false;
    }
    if (form.firstResponseMinutes == null && form.resolutionMinutes == null) {
      this.errorMessage = 'Set at least one target (first response or resolution).';
      return false;
    }
    this.errorMessage = '';
    return true;
  }

  private applyForm(p: IzzySLAPolicyEntity, form: PolicyForm): void {
    p.Name = form.name.trim();
    p.Priority = form.priority;
    p.FirstResponseTargetMinutes = form.firstResponseMinutes ?? null;
    p.ResolutionTargetMinutes = form.resolutionMinutes ?? null;
    p.BusinessHoursOnly = form.businessHoursOnly;
    p.EscalationPersonID = form.escalationPersonId || null;
    p.EscalationTeamID = form.escalationTeamId || null;
    p.SortOrder = form.sortOrder ?? 0;
    p.Status = form.status;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadSLAPoliciesComponent(): void {}
